import { useCallback, useMemo, useState } from "react"
import { Alert, Box, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ERROR, LOADING, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import type { SkillCatalogSkill, SkillSection } from "connected-components/skills/types"
import { useDeleteOwnSkill, useSkillCatalog } from "resources/skills"
import { useSetSkillEnabled, useSkillSettings } from "resources/skill-settings"
import type { KellyPaneLayout } from "../kelly-pane-page"
import { KellySkillsPage } from "./kelly-skills-page"
import { applyHeldOrder, orderSkillsForDisplay } from "./order-skills"
import { SkillDetail } from "./skill-detail"
import { SkillList } from "./skill-list"

const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const EMPTY_SX = { color: "text.secondary", py: 1.5 } satisfies SxProps<Theme>

interface HeldOrderSkillListProps {
  sections: SkillSection[]
  categoryOrder: string[]
  ownSkillsFailed: boolean
  sharedSkillsFailed: boolean
  sharedSkillsLoading: boolean
  isSkillEnabled: (skillId: string) => boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
  pendingSkillIds: ReadonlySet<string>
  onSelect: (skill: SkillCatalogSkill) => void
}

function HeldOrderSkillList({ sections, categoryOrder, ownSkillsFailed, sharedSkillsFailed, sharedSkillsLoading, isSkillEnabled, onToggle, pendingSkillIds, onSelect }: HeldOrderSkillListProps) {
  const [heldIdsBySection, setHeldIdsBySection] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(sections.map((section) => [section.id, orderSkillsForDisplay(section.skills, isSkillEnabled).map((skill) => skill.id)])),
  )
  const sectionsWithoutHeldOrder = sections.filter((section) => section.skills.length > 0 && !heldIdsBySection[section.id]?.length)
  if (sectionsWithoutHeldOrder.length > 0) {
    setHeldIdsBySection({
      ...heldIdsBySection,
      ...Object.fromEntries(sectionsWithoutHeldOrder.map((section) => [section.id, orderSkillsForDisplay(section.skills, isSkillEnabled).map((skill) => skill.id)])),
    })
  }
  const orderedSections = useMemo(
    () => sections.map((section) => ({ ...section, skills: applyHeldOrder(section.skills, heldIdsBySection[section.id] ?? []) })),
    [sections, heldIdsBySection],
  )
  return (
    <SkillList
      sections={orderedSections}
      categoryOrder={categoryOrder}
      ownSkillsFailed={ownSkillsFailed}
      sharedSkillsFailed={sharedSkillsFailed}
      sharedSkillsLoading={sharedSkillsLoading}
      isSkillEnabled={isSkillEnabled}
      onToggle={onToggle}
      pendingSkillIds={pendingSkillIds}
      onSelect={onSelect}
    />
  )
}

interface SkillsPageBodyProps {
  catalogStatus: DataStatus
  ownStatus: DataStatus
  settingsStatus: DataStatus
  sections: SkillSection[]
  selectedSkill: SkillCatalogSkill | null
  categoryOrder: string[]
  layout: KellyPaneLayout
  isSkillEnabled: (skillId: string) => boolean
  sharedSkillsFailed: boolean
  sharedSkillsLoading: boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
  onDelete: (skill: SkillCatalogSkill) => void
  pendingSkillIds: ReadonlySet<string>
  onSelect: (skill: SkillCatalogSkill) => void
  deletingSkillId: string | null
}

function SkillsPageBody({ catalogStatus, ownStatus, settingsStatus, sections, selectedSkill, categoryOrder, layout, isSkillEnabled, sharedSkillsFailed, sharedSkillsLoading, onToggle, onDelete, pendingSkillIds, onSelect, deletingSkillId }: SkillsPageBodyProps) {
  if (catalogStatus === ERROR) {
    return <Alert severity="error" data-testid="kelly-skills-catalog-error">The skills catalog did not load. Try again later.</Alert>
  }
  if (settingsStatus === ERROR) {
    return <Alert severity="error" data-testid="kelly-skills-settings-error">Your skill settings did not load. Try again later.</Alert>
  }
  if (catalogStatus !== OK || settingsStatus !== OK) {
    return <Box sx={LOADING_SX}><CircularProgress size={20} aria-label="Loading skills" /></Box>
  }
  if (sections.length === 0 && !sharedSkillsFailed && !sharedSkillsLoading) {
    return (
      <Typography sx={EMPTY_SX} data-testid="kelly-skills-empty">
        {ownStatus === ERROR
          ? "Your own skills did not load, and no skills are published to this account. Try again later."
          : "No skills are published to this account."}
      </Typography>
    )
  }
  if (selectedSkill) {
    return (
      <SkillDetail
        key={selectedSkill.id}
        skill={selectedSkill}
        layout={layout}
        enabled={isSkillEnabled(selectedSkill.id)}
        isSaving={pendingSkillIds.has(selectedSkill.id)}
        isDeleting={deletingSkillId === selectedSkill.id}
        onToggle={onToggle}
        onDelete={onDelete}
      />
    )
  }
  return (
    <HeldOrderSkillList
      sections={sections}
      categoryOrder={categoryOrder}
      ownSkillsFailed={ownStatus === ERROR}
      sharedSkillsFailed={sharedSkillsFailed}
      sharedSkillsLoading={sharedSkillsLoading}
      isSkillEnabled={isSkillEnabled}
      onToggle={onToggle}
      pendingSkillIds={pendingSkillIds}
      onSelect={onSelect}
    />
  )
}

interface SkillsPageContentProps {
  layout: KellyPaneLayout
}

export function SkillsPageContent({ layout }: SkillsPageContentProps) {
  const { sections, categoryOrder, status: catalogStatus, ownStatus, sharedStatus } = useSkillCatalog()
  const { status: settingsStatus, isSkillEnabled, settingFor } = useSkillSettings()
  const { setSkillEnabled, pendingSkillIds } = useSetSkillEnabled()
  const { deleteSkill, deletingSkillId } = useDeleteOwnSkill()
  const skillById = useMemo(() => {
    const map = new Map<string, SkillCatalogSkill>()
    sections.forEach((section) => section.skills.forEach((skill) => map.set(skill.id, skill)))
    return map
  }, [sections])
  const getSkillEnabled = useCallback((skillId: string) => {
    const entry = skillById.get(skillId)
    if (entry?.manageOnly) return false
    if (entry?.tier === "shared") return settingFor(skillId) ?? entry.enabled ?? false
    return isSkillEnabled(skillId)
  }, [skillById, settingFor, isSkillEnabled])
  const analytics = useAnalytics()
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null)
  const selectedSkill = useMemo(() => {
    const found = sections.flatMap((section) => section.skills).find((skill) => skill.id === selectedSkillId)
    return found == null || found.manageOnly ? null : found
  }, [sections, selectedSkillId])
  const onSelect = useCallback((skill: SkillCatalogSkill) => setSelectedSkillId(skill.id), [])
  const backToList = useCallback(() => setSelectedSkillId(null), [])

  const onToggle = useCallback((skill: SkillCatalogSkill, enabled: boolean) => {
    analytics.track(enabled ? SKILL_EVENTS.SKILL_ENABLED : SKILL_EVENTS.SKILL_DISABLED, { skillId: skill.id, tier: skill.tier })
    void setSkillEnabled({ skillId: skill.id, skillName: skill.name, enabled }).catch(() => undefined)
  }, [analytics, setSkillEnabled])

  const onDelete = useCallback((skill: SkillCatalogSkill) => {
    void deleteSkill(skill.id)
      .then(() => setSelectedSkillId((current) => (current === skill.id ? null : current)))
      .catch(() => undefined)
  }, [deleteSkill])

  // The shell owns the header, so the back destination is raised to this level: it is the only component
  // that knows whether a skill is open.
  return (
    <KellySkillsPage onBack={selectedSkill ? backToList : undefined} scrollResetKey={selectedSkillId ?? ""}>
      <SkillsPageBody
        catalogStatus={catalogStatus}
        ownStatus={ownStatus}
        settingsStatus={settingsStatus}
        sections={sections}
        selectedSkill={selectedSkill}
        categoryOrder={categoryOrder}
        layout={layout}
        isSkillEnabled={getSkillEnabled}
        sharedSkillsFailed={sharedStatus === ERROR}
        sharedSkillsLoading={sharedStatus === LOADING}
        onToggle={onToggle}
        onDelete={onDelete}
        pendingSkillIds={pendingSkillIds}
        onSelect={onSelect}
        deletingSkillId={deletingSkillId}
      />
    </KellySkillsPage>
  )
}
